import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  setDoc,
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import moment from 'moment';

const readDate = (date) => {
  if (!date) return null;
  if (date?.toDate) return date.toDate();
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (date) => {
  const d = readDate(date);
  return d ? moment(d).format('DD/MM/YYYY HH:mm') : 'N/A';
};

const CrewAuditions = () => {
  const [auditions, setAuditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAudition, setSelectedAudition] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAuditions = async () => {
    setLoading(true);

    try {
      const q = query(collection(db, 'crew_auditions'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setAuditions(data);
    } catch (error) {
      console.error('Erreur chargement auditions crew:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditions();
  }, []);

  const updateStatus = async (audition, status) => {
    await setDoc(
      doc(db, 'crew_auditions', audition.id),
      {
        status,
        updatedAt: new Date(),
      },
      { merge: true }
    );

    await fetchAuditions();
    setSelectedAudition((prev) => (prev ? { ...prev, status } : prev));
  };

  const filteredData = auditions.filter((item) => {
    const haystack = [
      item.firstName,
      item.lastName,
      item.email,
      item.phone,
      item.parentName,
      item.parentPhone,
      item.status,
      item.danceYears,
    ]
      .join(' ')
      .toLowerCase();

    const searchOk = !searchTerm || haystack.includes(searchTerm.toLowerCase());
    const statusOk = statusFilter === 'all' || item.status === statusFilter;

    return searchOk && statusOk;
  });

  const csvData = filteredData.map((item) => ({
    Date: formatDate(item.createdAt),
    Prénom: item.firstName,
    Nom: item.lastName,
    'Date naissance': item.dateOfBirth,
    Mineur: item.isMinor ? 'Oui' : 'Non',
    'Années danse': item.danceYears,
    Téléphone: item.phone,
    Email: item.email,
    Parent: item.parentName,
    'Téléphone parent': item.parentPhone,
    'Email parent': item.parentEmail,
    Statut: item.status,
    Message: item.message,
  }));

  const statusLabel = (status) => {
    if (status === 'contacted') return 'Contacté';
    if (status === 'confirmed') return 'Confirmé';
    if (status === 'archived') return 'Archivé';
    return 'Nouveau';
  };

  const statusColor = (status) => {
    if (status === 'confirmed') return 'success';
    if (status === 'contacted') return 'info';
    if (status === 'archived') return 'default';
    return 'warning';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Auditions Crew
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Rechercher un danseur, email, téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                label="Statut"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="new">Nouveau</MenuItem>
                <MenuItem value="contacted">Contacté</MenuItem>
                <MenuItem value="confirmed">Confirmé</MenuItem>
                <MenuItem value="archived">Archivé</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <CSVLink
              data={csvData}
              filename={`auditions-crew-${moment().format('YYYYMMDD-HHmm')}.csv`}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ mr: 1 }}>
                Exporter CSV
              </Button>
            </CSVLink>

            <Tooltip title="Actualiser">
              <IconButton onClick={fetchAuditions}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Danseur</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Âge / Mineur</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Années danse</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Parent</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((audition) => (
                    <TableRow hover key={audition.id}>
                      <TableCell>
                        <Tooltip title="Voir détails">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAudition(audition);
                              setDialogOpen(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>

                      <TableCell>{formatDate(audition.createdAt)}</TableCell>
                      <TableCell>
                        <strong>{audition.firstName} {audition.lastName}</strong>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          Né(e) le {audition.dateOfBirth || 'N/A'}
                        </div>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={audition.isMinor ? 'Mineur' : 'Majeur'}
                          size="small"
                          color={audition.isMinor ? 'warning' : 'success'}
                        />
                      </TableCell>

                      <TableCell>{audition.danceYears || 0} an(s)</TableCell>

                      <TableCell>
                        <div>{audition.email}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{audition.phone}</div>
                      </TableCell>

                      <TableCell>
                        {audition.isMinor ? (
                          <>
                            <div>{audition.parentName || 'N/A'}</div>
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              {audition.parentPhone}
                            </div>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={statusLabel(audition.status)}
                          color={statusColor(audition.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails audition crew</DialogTitle>

        <DialogContent>
          {selectedAudition && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedAudition.firstName} {selectedAudition.lastName}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography><strong>Date inscription :</strong> {formatDate(selectedAudition.createdAt)}</Typography>
                  <Typography><strong>Date naissance :</strong> {selectedAudition.dateOfBirth}</Typography>
                  <Typography><strong>Mineur :</strong> {selectedAudition.isMinor ? 'Oui' : 'Non'}</Typography>
                  <Typography><strong>Années de danse :</strong> {selectedAudition.danceYears}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography><strong>Email :</strong> {selectedAudition.email}</Typography>
                  <Typography><strong>Téléphone :</strong> {selectedAudition.phone}</Typography>
                  <Typography><strong>Statut :</strong> {statusLabel(selectedAudition.status)}</Typography>
                </Grid>

                {selectedAudition.isMinor && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
                      Contact parent
                    </Typography>
                    <Typography><strong>Nom :</strong> {selectedAudition.parentName}</Typography>
                    <Typography><strong>Téléphone :</strong> {selectedAudition.parentPhone}</Typography>
                    <Typography><strong>Email :</strong> {selectedAudition.parentEmail || 'N/A'}</Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
                    Message
                  </Typography>
                  <Typography>{selectedAudition.message || 'Aucun message'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => updateStatus(selectedAudition, 'contacted')}
                    sx={{ mr: 1 }}
                  >
                    Marquer contacté
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => updateStatus(selectedAudition, 'confirmed')}
                    sx={{ mr: 1 }}
                  >
                    Confirmer
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => updateStatus(selectedAudition, 'archived')}
                  >
                    Archiver
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewAuditions;